import { FC } from "react";
import { Control } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
} from "@altimateai/lego";
import { IconHash } from "@tabler/icons-react";
import { useSlackChannels } from "@/pages/alerts/useSlackChannels";
import { ScheduleFormValues } from "./useScheduleForm";

// Radix Select doesn't allow empty string values, so we use an explicit
// sentinel for the "no Slack channel" option and translate it back to ""
// (the empty form value) in the onChange handler.
const NO_WEBHOOK_SENTINEL = "__none__";

interface ScheduleSlackFieldProps {
  control: Control<ScheduleFormValues>;
  open: boolean;
}

const ScheduleSlackField: FC<ScheduleSlackFieldProps> = ({ control, open }) => {
  const { slackChannels: webhookChannels } = useSlackChannels(open);

  if (!webhookChannels || webhookChannels.length === 0) return null;

  return (
    <FormField
      control={control}
      name="slackWebhookId"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Slack Channel (optional)</FormLabel>
          <Select
            value={field.value || NO_WEBHOOK_SENTINEL}
            onValueChange={val => field.onChange(val === NO_WEBHOOK_SENTINEL ? "" : val)}
          >
            <FormControl>
              <SelectTrigger>
                <SelectValue placeholder="Select a webhook channel" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value={NO_WEBHOOK_SENTINEL}>No Slack channel</SelectItem>
              {webhookChannels.map(ch => (
                <SelectItem key={ch.id} value={String(ch.id)}>
                  <Stack direction="row" align="center" gap="xs">
                    <IconHash className="al-h-3 al-w-3" />
                    {ch.config?.name}
                  </Stack>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};

export default ScheduleSlackField;
